import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useHeaderScroll } from './Header/hooks/useHeaderScroll';
import { useMobileMenu } from './Header/hooks/useMobileMenu';
import NavigationMenu from './Header/components/NavigationMenu';
import MobileMenu from './Header/components/MobileMenu';
import LanguageSelector from './LanguageSelector';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Header.css';

const Header = () => {
  const [isWebsiteMenuOpen, setIsWebsiteMenuOpen] = useState(false);
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);
  const [isMoreInfoMenuOpen, setIsMoreInfoMenuOpen] = useState(false);
  const [mobileWebsiteExpanded, setMobileWebsiteExpanded] = useState(false);
  const [mobileServicesExpanded, setMobileServicesExpanded] = useState(false);
  const [mobileMoreInfoExpanded, setMobileMoreInfoExpanded] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomepage = location.pathname === '/';
  const isScrolled = useHeaderScroll(isHomepage);
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu } = useMobileMenu();
  const { user, userData, signIn, signOut, loading: authLoading, isSalesPartner, isWebsiteOwner } = useAuth();
  const { locale } = useLanguage();

  const handleLinkClick = () => {
    closeMobileMenu();
    setIsWebsiteMenuOpen(false);
    setIsServicesMenuOpen(false);
    setIsMoreInfoMenuOpen(false);
    setMobileWebsiteExpanded(false);
    setMobileServicesExpanded(false);
    setMobileMoreInfoExpanded(false);
  };

  const handleWebsiteMenuToggle = (e) => {
    // On click, navigate to All Packages page
    e.preventDefault();
    navigate('/website');
    setIsWebsiteMenuOpen(false);
    handleLinkClick();
  };

  const handleServicesMenuToggle = (e) => {
    // On click, navigate to All Services page
    e.preventDefault();
    navigate('/services');
    setIsServicesMenuOpen(false);
    handleLinkClick();
  };

  const handleMoreInfoMenuToggle = (e) => {
    e.preventDefault();
    // No specific page for More Info, just toggle if needed or do nothing if it's purely hover
    // But for consistency let's just make it toggle for mobile/touch
    setIsMoreInfoMenuOpen(!isMoreInfoMenuOpen);
  };

  const handleWebsiteSubmenuClick = (path) => {
    navigate(path);
    setIsWebsiteMenuOpen(false);
    closeMobileMenu();
  };

  const handleServicesSubmenuClick = (path) => {
    navigate(path);
    setIsServicesMenuOpen(false);
    closeMobileMenu();
  };

  const handleMoreInfoSubmenuClick = (path) => {
    navigate(path);
    setIsMoreInfoMenuOpen(false);
    closeMobileMenu();
  };

  const handleGoogleSignIn = async () => {
    try {
      await signIn();
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Failed to sign in. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (userData?.name) return userData.name;
    if (user?.displayName) return user.displayName;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  // Reset avatar error when user changes
  useEffect(() => {
    if (user) {
      setAvatarError(false);
    }
  }, [user]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-auth-container')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  return (
    <>
      <div
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
        aria-label="Close menu"
      ></div>
      <header className={`header ${isHomepage ? 'homepage-header' : ''} ${isScrolled ? 'scrolled' : ''} ${locale !== 'en' ? 'header--local-locale' : ''}`} data-scrolled={isScrolled}>
        <div className="container">
          <div className="header-content">
            <Link to="/" className="logo" onClick={handleLinkClick}>
              <img
                src="/logo/ak_official.webp"
                alt="Apka Khata Logo"
                className="logo-icon"
              />
              <h2>ApkaKhata</h2>
            </Link>
            <nav className={`nav ${isMobileMenuOpen ? 'open' : ''}`}>
              <NavigationMenu
                isWebsiteMenuOpen={isWebsiteMenuOpen}
                setIsWebsiteMenuOpen={setIsWebsiteMenuOpen}
                isServicesMenuOpen={isServicesMenuOpen}
                setIsServicesMenuOpen={setIsServicesMenuOpen}
                isMoreInfoMenuOpen={isMoreInfoMenuOpen}
                setIsMoreInfoMenuOpen={setIsMoreInfoMenuOpen}
                handleWebsiteMenuToggle={handleWebsiteMenuToggle}
                handleServicesMenuToggle={handleServicesMenuToggle}
                handleMoreInfoMenuToggle={handleMoreInfoMenuToggle}
                handleWebsiteSubmenuClick={handleWebsiteSubmenuClick}
                handleServicesSubmenuClick={handleServicesSubmenuClick}
                handleMoreInfoSubmenuClick={handleMoreInfoSubmenuClick}
                handleLinkClick={handleLinkClick}
              />

              {/* Mobile Menu Structure */}
              <MobileMenu
                isMobileMenuOpen={isMobileMenuOpen}
                mobileWebsiteExpanded={mobileWebsiteExpanded}
                setMobileWebsiteExpanded={setMobileWebsiteExpanded}
                mobileServicesExpanded={mobileServicesExpanded}
                setMobileServicesExpanded={setMobileServicesExpanded}
                mobileMoreInfoExpanded={mobileMoreInfoExpanded}
                setMobileMoreInfoExpanded={setMobileMoreInfoExpanded}
                handleLinkClick={handleLinkClick}
                user={user}
                authLoading={authLoading}
                avatarError={avatarError}
                setAvatarError={setAvatarError}
                getUserDisplayName={getUserDisplayName}
                handleGoogleSignIn={handleGoogleSignIn}
                handleLogout={handleLogout}
                isSalesPartner={isSalesPartner}
                isWebsiteOwner={isWebsiteOwner}
              />
            </nav>
            <div className="header-actions">
              <div className="header-auth-desktop">
                {!authLoading && (
                  <>
                    {!user ? (
                      <button
                        onClick={handleGoogleSignIn}
                        className="google-signin-button"
                        aria-label="Sign in with Google"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" className="google-icon">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span>Sign in with Google</span>
                      </button>
                    ) : (
                      <div className="user-auth-container">
                        <div
                          className="user-profile-card"
                          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="user-avatar">
                            {user?.photoURL && !avatarError ? (
                              <img
                                src={user.photoURL}
                                alt={getUserDisplayName()}
                                className="avatar-image"
                                onError={() => setAvatarError(true)}
                                loading="lazy"
                              />
                            ) : (
                              <span className="avatar-initial">{getUserDisplayName().charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="user-info">
                            <span className="user-name-text">{getUserDisplayName()}</span>
                            {user?.email && (
                              <span className="user-email-text">{user.email}</span>
                            )}
                          </div>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            className={`user-menu-chevron ${isUserMenuOpen ? 'open' : ''}`}
                          >
                            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className={`user-menu-dropdown ${isUserMenuOpen ? 'show' : ''}`}>
                          {user?.email === 'amit.mca.pu@gmail.com' && (
                            <Link
                              to="/admin-dashboard/call-requests/website-owner"
                              className="user-menu-item dashboard-menu-item"
                              onClick={() => setIsUserMenuOpen(false)}
                              aria-label="Admin Dashboard"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                              </svg>
                              <span>Admin Dashboard</span>
                            </Link>
                          )}
                          {isSalesPartner && (
                            <Link
                              to="/partner"
                              className="user-menu-item dashboard-menu-item"
                              onClick={() => setIsUserMenuOpen(false)}
                              aria-label="Sales Partner Dashboard"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                              <span>Sales Partner Dashboard</span>
                            </Link>
                          )}
                          {isWebsiteOwner && (
                            <Link
                              to="/owner"
                              className="user-menu-item dashboard-menu-item"
                              onClick={() => setIsUserMenuOpen(false)}
                              aria-label="Website Owner Dashboard"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                              </svg>
                              <span>Website Owner Dashboard</span>
                            </Link>
                          )}
                          <button
                            onClick={handleLogout}
                            className="user-menu-item logout-menu-item"
                            aria-label="Logout"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                              <polyline points="16 17 21 12 16 7"></polyline>
                              <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            <span>Logout</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <LanguageSelector />
              <button
                className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
